import {
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Button,
  FormHelperText,
  VStack,
  InputLeftElement,
  InputGroup,
  CheckboxGroup,
  Stack,
  Checkbox,
  Divider,
  Alert,
  AlertIcon
} from '@chakra-ui/react';
import { Formik, Field, Form, FormikProps } from 'formik';
import {
  useNewTeacherContext,
  TeacherProfileVariant,
  PARTICIPATION_TYPES,
  ParticipationType
} from '../../contexts/NewTeacherProvider';

const VERTICAL_LABELS: Record<ParticipationType, string> = {
  university: 'Universidades',
  languages: 'Idiomas',
  schools: 'Colegios secundarios'
};

interface FormValues {
  participatingTypes: ParticipationType[];
  profiles: Record<ParticipationType, TeacherProfileVariant>;
  busyDays: string[];
  inPersonClasses: boolean;
  inPersonClassesMessage: string;
  virtualClasses: boolean;
  virtualClassesMessage: string;
  weeksInAdvance: number;
}

const emptyVariant = (): TeacherProfileVariant => ({
  description: '',
  fullDescription: '',
  pricePerHour: 0
});

const buildInitialProfiles = (
  profiles: Record<string, TeacherProfileVariant>
): Record<ParticipationType, TeacherProfileVariant> => {
  return PARTICIPATION_TYPES.reduce(
    (acc, type) => ({ ...acc, [type]: profiles[type] ?? emptyVariant() }),
    {} as Record<ParticipationType, TeacherProfileVariant>
  );
};

const validate = (values: FormValues): Record<string, string> => {
  const errors: Record<string, string> = {};

  if (values.participatingTypes.length === 0) {
    errors.participatingTypes = 'Elegí al menos una vertical';
  }

  values.participatingTypes.forEach((type) => {
    const variant = values.profiles[type];

    if (!variant.description || variant.description.length < 2) {
      errors[`profiles.${type}.description`] = 'Requerido';
    } else if (variant.description.length > 800) {
      errors[`profiles.${type}.description`] = 'Demasiado largo!';
    }

    if (!variant.fullDescription || variant.fullDescription.length < 2) {
      errors[`profiles.${type}.fullDescription`] = 'Requerido';
    } else if (variant.fullDescription.length > 4000) {
      errors[`profiles.${type}.fullDescription`] = 'Demasiado largo!';
    }

    if (!variant.pricePerHour) {
      errors[`profiles.${type}.pricePerHour`] = 'Requerido';
    }
  });

  if (!values.virtualClasses && !values.inPersonClasses) {
    errors.virtualClasses = 'Elegí al menos una modalidad';
  }

  if (!values.weeksInAdvance || values.weeksInAdvance < 1) {
    errors.weeksInAdvance = 'Requerido';
  }

  return errors;
};

const ProfileData = () => {
  const { teacher, updateTeacher } = useNewTeacherContext();

  const handleSubmit = async (values: FormValues, actions: any) => {
    const profiles = values.participatingTypes.reduce(
      (acc, type) => ({ ...acc, [type]: values.profiles[type] }),
      {} as Record<string, TeacherProfileVariant>
    );

    await updateTeacher({
      step: teacher.step + 1,
      profiles,
      busyDays: values.busyDays,
      inPersonClasses: values.inPersonClasses,
      inPersonClassesMessage: values.inPersonClassesMessage,
      virtualClasses: values.virtualClasses,
      virtualClassesMessage: values.virtualClassesMessage,
      weeksInAdvance: values.weeksInAdvance
    });
    actions.setSubmitting(false);
  };

  const handleBack = async () => {
    await updateTeacher({ step: teacher.step - 1 });
  };

  return (
    <Formik
      initialValues={{
        participatingTypes: Object.keys(teacher.profiles) as ParticipationType[],
        profiles: buildInitialProfiles(teacher.profiles),
        busyDays: teacher.busyDays as string[],
        inPersonClasses: teacher.inPersonClasses as boolean,
        inPersonClassesMessage: teacher.inPersonClassesMessage as string,
        virtualClasses: teacher.virtualClasses as boolean,
        virtualClassesMessage: teacher.virtualClassesMessage as string,
        weeksInAdvance: teacher.weeksInAdvance
      }}
      validate={validate}
      onSubmit={handleSubmit}>
      {(props: FormikProps<FormValues>) => {
        const { errors, isSubmitting, setFieldValue, values } = props;
        const flatErrors = errors as unknown as Record<string, string>;

        return (
          <Form>
            <VStack spacing="12px" margin="1rem">
              <FormControl isRequired isInvalid={!!errors.participatingTypes}>
                <FormLabel>¿En qué verticales das clases?</FormLabel>
                <CheckboxGroup
                  value={values.participatingTypes}
                  onChange={(selected) => {
                    void setFieldValue('participatingTypes', selected);
                  }}>
                  <Stack spacing={[1, 5]} direction={['column', 'row']}>
                    {PARTICIPATION_TYPES.map((type) => (
                      <Checkbox key={type} value={type} required={false}>
                        {VERTICAL_LABELS[type]}
                      </Checkbox>
                    ))}
                  </Stack>
                </CheckboxGroup>
                <FormErrorMessage>{errors.participatingTypes}</FormErrorMessage>
                <FormHelperText>
                  Podés participar en más de una. Vas a poder elegir una descripción y un precio
                  distinto para cada una.
                </FormHelperText>
              </FormControl>

              {values.participatingTypes.map((type) => (
                <VStack key={type} width="100%" align="stretch" spacing="12px">
                  <Divider mt={4} />
                  <FormLabel fontWeight="bold">{VERTICAL_LABELS[type]}</FormLabel>
                  <Field name={`profiles.${type}.description`}>
                    {({ field }: { field: any }) => (
                      <FormControl
                        isRequired
                        isInvalid={!!flatErrors[`profiles.${type}.description`]}>
                        <FormLabel>Descripción Resumen</FormLabel>
                        <Input {...field} placeholder="Soy estudiante de Ing. Química en el ITBA" />
                        <FormErrorMessage>
                          {flatErrors[`profiles.${type}.description`] as string}
                        </FormErrorMessage>
                        <FormHelperText>
                          Esta es lo primero que ve sobre vos un estudiante cuando le aparecen todas
                          las opciones de profes para esta vertical. Apunta a no más de 20 palabras!
                        </FormHelperText>
                      </FormControl>
                    )}
                  </Field>
                  <Field name={`profiles.${type}.fullDescription`}>
                    {({ field }: { field: any }) => (
                      <FormControl
                        isRequired
                        isInvalid={!!flatErrors[`profiles.${type}.fullDescription`]}>
                        <FormLabel>Descripción Completa</FormLabel>
                        <Input {...field} placeholder="Doy clases particulares hace ..." />
                        <FormErrorMessage>
                          {flatErrors[`profiles.${type}.fullDescription`] as string}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                  <Field name={`profiles.${type}.pricePerHour`}>
                    {({ field }: { field: any }) => (
                      <FormControl
                        isRequired
                        isInvalid={!!flatErrors[`profiles.${type}.pricePerHour`]}>
                        <FormLabel>Precio por hora de clase</FormLabel>
                        <InputGroup>
                          <InputLeftElement pointerEvents="none" color="gray.300" fontSize="1.2em">
                            $
                          </InputLeftElement>
                          <Input {...field} placeholder="Precio por hora" type="number" />
                        </InputGroup>
                        <FormErrorMessage>
                          {flatErrors[`profiles.${type}.pricePerHour`] as string}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                </VStack>
              ))}

              <Divider mt={4} />
              <FormLabel>Tipos de clases que das: Virtual y/o Presencial</FormLabel>
              {errors.virtualClasses && (
                <Alert status="error">
                  <AlertIcon />
                  {errors.virtualClasses}
                </Alert>
              )}
              <Field name="virtualClasses">
                {({ field }: { field: any }) => {
                  return (
                    <FormControl>
                      <Checkbox
                        name="virtualClasses"
                        required={false}
                        isChecked={field?.value}
                        value={field.value}
                        onChange={() => {
                          void setFieldValue('virtualClasses', !field?.value);
                        }}>
                        Clases Virtuales
                      </Checkbox>
                    </FormControl>
                  );
                }}
              </Field>
              <Field name="virtualClassesMessage">
                {({ field }: { field: any }) => {
                  return (
                    <FormControl>
                      <FormLabel>¿Cómo das las clases virtuales?</FormLabel>
                      <Input {...field} disabled={!values.virtualClasses} />
                      <FormHelperText>
                        En una oración describí que herramientas y modalidad usas. Algunos ejemplos:
                        "Uso una tableta digitalizadora/iPad/Tablet para escribrir", "Uso el mouse
                        para escribir en un pizarron virtual" y "Armo presentaciones para explicar
                        desde ahi".
                      </FormHelperText>
                    </FormControl>
                  );
                }}
              </Field>
              <Field name="inPersonClasses">
                {({ field }: { field: any }) => {
                  return (
                    <FormControl>
                      <Checkbox
                        name="inPersonClasses"
                        required={false}
                        isChecked={field?.value}
                        value={field.value}
                        onChange={() => {
                          void setFieldValue('inPersonClasses', !field?.value);
                        }}>
                        Clases presenciales
                      </Checkbox>
                    </FormControl>
                  );
                }}
              </Field>
              <Field name="inPersonClassesMessage">
                {({ field }: { field: any }) => {
                  return (
                    <FormControl>
                      <FormLabel>¿Cómo das las clases presenciales?</FormLabel>
                      <Input {...field} disabled={!values.inPersonClasses} />
                      <FormHelperText>
                        En una oración describi dónde y qué días das clases presenciales. Por
                        ejemplo "Los Viernes en Palermo, zona Plaza Italia CABA."
                      </FormHelperText>
                    </FormControl>
                  );
                }}
              </Field>
              <Field name="weeksInAdvance">
                {({ field }: { field: any }) => {
                  return (
                    <FormControl isInvalid={!!errors.weeksInAdvance}>
                      <FormLabel>
                        ¿Cuántas semanas querés que se vean en tu calendario cada vez que los
                        alumnos entren a tu perfil?
                      </FormLabel>
                      <Input {...field} type="number" min={1} />
                      <FormErrorMessage>{errors.weeksInAdvance}</FormErrorMessage>
                      <FormHelperText>Mínimo 1 y por defecto 4 semanas</FormHelperText>
                    </FormControl>
                  );
                }}
              </Field>
              <Stack display="flex" flexDirection="row" spacing={6}>
                <Button mt={4} variant="outline" colorScheme="blue" onClick={handleBack}>
                  Atras
                </Button>
                <Button mt={4} colorScheme="blue" isLoading={isSubmitting} type="submit">
                  Siguiente
                </Button>
              </Stack>
            </VStack>
          </Form>
        );
      }}
    </Formik>
  );
};

export default ProfileData;
